import { useMemo, useState } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/table";
import { Checkbox } from "@/ui/checkbox";
import { MultiSelect, type MultiSelectOption } from "@/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Button } from "@/ui/button";
import styles from "./ExplorerColumnsTable.module.scss";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  DQ_CHECKS,
  DQ_CHECK_BY_NAME,
  type DqCheckEntry,
  type DqCheckName,
  buildSimpleDqValue,
  isDqCheckName,
} from "@/features/adp/constants/dataQualityChecks";
import DqCheckDialog from "./DqCheckDialog";

export type ExplorerColumnRow = {
  id: string;
  selected: boolean;
  columnName: string;
  dataType: string;
  primaryKey: boolean;
  dqChecks: DqCheckEntry[];
};

type ExplorerColumnsTableProps = {
  rows: ExplorerColumnRow[];
  dataTypeOptions: string[];
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (rowId: string, checked: boolean) => void;
  onTogglePrimaryKey: (rowId: string, checked: boolean) => void;
  onDataTypeChange: (rowId: string, value: string) => void;
  onDqChecksChange: (rowId: string, value: DqCheckEntry[]) => void;
};

const DQ_OPTIONS: MultiSelectOption[] = DQ_CHECKS.map((def) => ({
  label: def.name,
  value: def.name,
}));

interface PendingDqCheck {
  rowId: string;
  name: DqCheckName;
}

interface DataQualityCellProps {
  rowId: string;
  selectedChecks: DqCheckEntry[];
  onChange: (next: DqCheckEntry[]) => void;
  onModalOpen: (pending: PendingDqCheck) => void;
}

function DataQualityCell({
  rowId,
  selectedChecks,
  onChange,
  onModalOpen,
}: DataQualityCellProps) {
  const selectedNames = useMemo(
    () => selectedChecks.map((entry) => entry.name),
    [selectedChecks],
  );

  const handleValueChange = (next: string[]) => {
    const validNext = next.filter(isDqCheckName);

    // Detect added vs removed by diffing against current selection.
    const added = validNext.filter((n) => !selectedNames.includes(n));
    const removed = selectedNames.filter((n) => !validNext.includes(n));

    if (removed.length > 0) {
      onChange(selectedChecks.filter((entry) => !removed.includes(entry.name)));
      return;
    }

    if (added.length === 0) return;

    // MultiSelect can theoretically batch multiple adds (e.g. select-all);
    // process simple ones immediately and queue the first modal-driven one.
    let current = selectedChecks;
    let modalQueued: DqCheckName | null = null;

    for (const name of added) {
      const def = DQ_CHECK_BY_NAME[name];
      if (def.modal) {
        if (!modalQueued) modalQueued = name;
        continue;
      }
      const simple = buildSimpleDqValue(name);
      if (simple == null) continue;
      current = [...current, { name, value: simple }];
    }

    if (current !== selectedChecks) onChange(current);
    if (modalQueued) onModalOpen({ rowId, name: modalQueued });
  };

  return (
    <MultiSelect
      options={DQ_OPTIONS}
      value={selectedNames}
      onValueChange={handleValueChange}
      placeholder="Select Data Quality"
      searchable={false}
      hideSelectAll
    />
  );
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

function getHeaderClassName(columnId: string): string {
  if (columnId === "selected") return `${styles.headerCell} ${styles.selectColumnCell}`;
  if (columnId === "columnName") return `${styles.headerCell} ${styles.columnNameColumnCell}`;
  if (columnId === "dataType") return `${styles.headerCell} ${styles.dataTypeColumnCell}`;
  if (columnId === "primaryKey") return `${styles.headerCell} ${styles.primaryKeyColumnCell}`;
  if (columnId === "dataQuality") return `${styles.headerCell} ${styles.dataQualityColumnCell}`;
  return styles.headerCell;
}

function getBodyClassName(columnId: string): string {
  if (columnId === "selected") return `${styles.bodyCell} ${styles.selectColumnCell}`;
  if (columnId === "columnName") return `${styles.bodyCell} ${styles.columnNameColumnCell}`;
  if (columnId === "dataType") return `${styles.bodyCell} ${styles.dataTypeColumnCell}`;
  if (columnId === "primaryKey") return `${styles.bodyCell} ${styles.primaryKeyColumnCell}`;
  if (columnId === "dataQuality") return `${styles.bodyCell} ${styles.dataQualityColumnCell}`;
  return styles.bodyCell;
}

const ExplorerColumnsTable = ({
  rows,
  dataTypeOptions,
  onToggleAll,
  onToggleRow,
  onTogglePrimaryKey,
  onDataTypeChange,
  onDqChecksChange,
}: ExplorerColumnsTableProps) => {
  const allSelected = rows.length > 0 && rows.every((row) => row.selected);

  const [pendingDqCheck, setPendingDqCheck] = useState<PendingDqCheck | null>(null);

  const handleDqConfirm = (formattedValue: string) => {
    if (!pendingDqCheck) return;
    const row = rows.find((r) => r.id === pendingDqCheck.rowId);
    if (!row) {
      setPendingDqCheck(null);
      return;
    }
    onDqChecksChange(pendingDqCheck.rowId, [
      ...row.dqChecks,
      { name: pendingDqCheck.name, value: formattedValue },
    ]);
    setPendingDqCheck(null);
  };

  const columns = useMemo<ColumnDef<ExplorerColumnRow>[]>(
    () => [
      {
        id: "selected",
        header: () => (
          <div className={styles.checkboxCell}>
            <Checkbox
              checked={allSelected}
              onCheckedChange={(checked) => onToggleAll(checked === true)}
              aria-label="Select all columns"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className={styles.checkboxCell}>
            <Checkbox
              checked={row.original.selected}
              onCheckedChange={(checked) => onToggleRow(row.original.id, checked === true)}
              aria-label={`Select ${row.original.columnName}`}
            />
          </div>
        ),
      },
      {
        accessorKey: "columnName",
        header: "Field Name",
        cell: ({ row }) => (
          <div className={styles.fieldNameCell}>
            <span className={styles.fieldNameText}>{row.original.columnName}</span>
            <span className={styles.fieldNameColon} aria-hidden="true">:</span>
          </div>
        ),
      },
      {
        accessorKey: "dataType",
        header: "Data Type",
        cell: ({ row }) => (
          <div className={styles.typeCell}>
            <Select
              value={row.original.dataType}
              onValueChange={(value) => onDataTypeChange(row.original.id, value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select data type" />
              </SelectTrigger>
              <SelectContent>
                {dataTypeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ),
      },
      {
        id: "primaryKey",
        header: () => <div className={styles.primaryKeyCell}>Primary Key</div>,
        cell: ({ row }) => (
          <div className={styles.primaryKeyCell}>
            <Checkbox
              checked={row.original.primaryKey}
              onCheckedChange={(checked) => onTogglePrimaryKey(row.original.id, checked === true)}
              aria-label={`Primary key for ${row.original.columnName}`}
            />
          </div>
        ),
      },
      {
        id: "dataQuality",
        header: "Data Quality",
        cell: ({ row }) => (
          <div className={styles.dqCell}>
            <DataQualityCell
              rowId={row.original.id}
              selectedChecks={row.original.dqChecks}
              onChange={(next) => onDqChecksChange(row.original.id, next)}
              onModalOpen={setPendingDqCheck}
            />
          </div>
        ),
      },
    ],
    [
      allSelected,
      dataTypeOptions,
      onDqChecksChange,
      onDataTypeChange,
      onToggleAll,
      onTogglePrimaryKey,
      onToggleRow,
    ],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    },
  });

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableScrollArea}>
        <table className={`w-full caption-bottom text-sm ${styles.table}`}>
          <colgroup>
            <col className={styles.selectColumn} />
            <col className={styles.columnNameColumn} />
            <col className={styles.dataTypeColumn} />
            <col className={styles.primaryKeyColumn} />
            <col className={styles.dataQualityColumn} />
          </colgroup>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={getHeaderClassName(header.column.id)}
                  >
                    {header.isPlaceholder
                      ? null
                      : typeof header.column.columnDef.header === "function"
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={getBodyClassName(cell.column.id)}
                    >
                      {typeof cell.column.columnDef.cell === "function"
                        ? cell.column.columnDef.cell(cell.getContext())
                        : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className={styles.emptyState}>
                  No columns available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>

      {rows.length > 0 && (
      <div className={styles.tableFooter}>
        <div className={styles.rowsLabel}>
          <span>Rows per page</span>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(value) =>
              table.setPagination({
                pageIndex: 0,
                pageSize: Number(value),
              })
            }
          >
            <SelectTrigger className={styles.pageSizeTrigger} iconName="unfold_more">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className={styles.pageText}>
          Page {table.getState().pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
        </span>
        <div className={styles.pageNavGroup}>
          <Button
            variant="outline"
            size="icon"
            className={styles.pageNavButton}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={styles.pageNavButton}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
      )}

      <DqCheckDialog
        checkName={pendingDqCheck?.name ?? null}
        onConfirm={handleDqConfirm}
        onCancel={() => setPendingDqCheck(null)}
      />
    </div>
  );
};

export default ExplorerColumnsTable;
